import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";
import BackLink from "@/components/BackLink";
import { EYEBROW, fieldClass, labelClass, primaryBtn, formatErr, GoldDivider } from "@/lib/authForm";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const token = useMemo(() => params.get("token") || "", [params]);
    const navigate = useNavigate();

    const [checking, setChecking] = useState(true);
    const [valid, setValid] = useState(false);
    const [emailMasked, setEmailMasked] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!token) {
            setChecking(false);
            return;
        }
        let cancelled = false;
        api.get("/auth/reset-password/check", { params: { token } })
            .then((r) => {
                if (!cancelled) {
                    setValid(true);
                    setEmailMasked(r.data?.email_masked || "");
                }
            })
            .catch(() => {
                if (!cancelled) setValid(false);
            })
            .finally(() => {
                if (!cancelled) setChecking(false);
            });
        return () => { cancelled = true; };
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (!token) {
            toast.error("Missing reset token");
            return;
        }
        if (password.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        if (password !== confirm) {
            toast.error("Passwords do not match");
            return;
        }
        setBusy(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: password });
            toast.success("Password updated — sign in with your new password");
            navigate("/login", { replace: true });
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#FAF9F6] font-['Outfit',system-ui,sans-serif] flex items-center justify-center px-6 py-12">
            <div className="relative w-full max-w-[440px]">
                <BackLink to="/login" className="mb-5">Back to sign in</BackLink>

                <div className="mb-7 text-center">
                    <Link to="/" className="inline-block transition-opacity hover:opacity-90">
                        <BrandLogo
                            size={36}
                            textClass="font-serif text-2xl font-semibold text-[#4A0E17]"
                            accentClass="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#C84B31]"
                        />
                    </Link>
                    <p className={`${EYEBROW} mt-6`} style={{ color: "#C9A227" }}>Account recovery</p>
                    <h1 className="mt-2 font-serif text-[2rem] font-semibold tracking-tight text-[#4A0E17]">
                        Set new password
                    </h1>
                    <GoldDivider className="mx-auto mt-4" />
                </div>

                {checking ? (
                    <p className="text-center text-sm font-medium text-[#8A8178]">Checking reset link…</p>
                ) : !token || !valid ? (
                    <div className="space-y-4 text-center" data-testid="reset-password-invalid">
                        <p className="text-sm font-medium text-[#C84B31]">
                            This reset link is invalid or has expired.
                        </p>
                        <Link
                            to="/forgot-password"
                            className="inline-block text-sm font-semibold text-[#C84B31] hover:underline"
                        >
                            Request a new reset link
                        </Link>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-5" data-testid="reset-password-form">
                        {emailMasked && (
                            <p className="text-center text-sm font-medium text-[#8A8178]">
                                Resetting password for <strong className="text-[#3D2817]">{emailMasked}</strong>
                            </p>
                        )}
                        <div className="space-y-2">
                            <Label htmlFor="password" className={labelClass}>New password</Label>
                            <div className="relative">
                                <Lock className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#8A8178]" />
                                <Input
                                    id="password"
                                    type={showPw ? "text" : "password"}
                                    autoComplete="new-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    minLength={8}
                                    className={`${fieldClass} pr-12`}
                                    placeholder="At least 8 characters"
                                    data-testid="reset-password-input"
                                />
                                <button
                                    type="button"
                                    className="absolute right-3.5 top-1/2 -translate-y-1/2 rounded-md p-1 text-[#8A8178] hover:text-[#4A0E17]"
                                    onClick={() => setShowPw((v) => !v)}
                                    aria-label={showPw ? "Hide password" : "Show password"}
                                    tabIndex={-1}
                                >
                                    {showPw ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                                </button>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="confirm" className={labelClass}>Confirm password</Label>
                            <div className="relative">
                                <Lock className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#8A8178]" />
                                <Input
                                    id="confirm"
                                    type={showPw ? "text" : "password"}
                                    autoComplete="new-password"
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    required
                                    minLength={8}
                                    className={fieldClass}
                                    placeholder="Re-enter password"
                                    data-testid="reset-confirm-input"
                                />
                            </div>
                        </div>
                        <Button type="submit" disabled={busy} className={primaryBtn} data-testid="reset-submit">
                            {busy ? "Saving…" : "Update password"}
                        </Button>
                    </form>
                )}
            </div>
        </div>
    );
}
